package top.wcpe.allincore.ui.runtime.render

import top.wcpe.allincore.layout.engine.Box
import top.wcpe.allincore.ui.runtime.element.ScrollableContainer
import top.wcpe.allincore.ui.runtime.element.UIElement
import top.wcpe.allincore.ui.runtime.render.UiRenderBridge
import top.wcpe.allincore.ui.runtime.theme.StateLayer
import top.wcpe.allincore.ui.runtime.theme.ThemeAccess

/**
 * 滚动容器渲染器（接入主题）：服务于所有 [ScrollableContainer] 子类
 * （ScrollView / VirtualList / 后续 PageView 等）。
 *
 * 背景由 DSL 显式 `background` 决定（不强制主题色，因滚动容器多用作内容容器）；
 * 滚动条 track/thumb 使用主题 `outlineVariant` / `outline`，并保留原半透明视觉（track 40% / thumb 80%）。
 *
 * 裁剪、子节点偏移、viewport/content 同步均由 [UIRenderer] 中 `ScrollableContainer` 统一分支负责，避免双写。
 */
@Suppress("MagicNumber")
open class ScrollableContainerRenderer : Renderer {

    override fun render(element: UIElement<*>, box: Box, bridge: UiRenderBridge) {
        val sc = element as ScrollableContainer<*>
        renderBackground(sc, box, bridge)
        renderScrollbar(sc, box, bridge)
    }

    // 原 Java package-private 扩展点；Kotlin 无 package-private，用 protected open
    // （JVM protected 同包亦可访问，故 UIRenderer 同包直接调用与 Java/Kotlin 子类 override 均兼容）。
    internal open fun renderBackground(sc: ScrollableContainer<*>, box: Box, bridge: UiRenderBridge) {
        val bg = sc.style().backgroundColor()
        if ((bg ushr 24) != 0) {
            bridge.fillRect(box.x(), box.y(), box.width(), box.height(), bg)
        }
    }

    /** 先画水平滚动条（仅在水平可滚时），再画垂直滚动条，确保垂直 thumb 在仅垂直可滚时为最后一笔。 */
    internal open fun renderScrollbar(sc: ScrollableContainer<*>, box: Box, bridge: UiRenderBridge) {
        if (box.width() <= 0f || box.height() <= 0f) {
            return
        }
        if (sc.scrollbarMode() == ScrollableContainer.ScrollbarMode.NEVER) {
            return
        }
        renderScrollbarX(sc, box, bridge)
        renderScrollbarY(sc, box, bridge)
    }

    private fun renderScrollbarY(sc: ScrollableContainer<*>, box: Box, bridge: UiRenderBridge) {
        val viewport = sc.viewportHeight()
        val content = sc.contentHeight()
        if (!shouldShow(sc, viewport, content) || content <= viewport) {
            return
        }
        val track = trackColor(sc)
        val thumb = thumbColor(sc)
        val bar = barWidth(sc)
        val trackX = box.x() + (box.width() - bar).coerceAtLeast(0f)
        val trackW = bar.coerceAtMost(box.width())
        val trackH = box.height()
        val thumbH = (viewport / content * trackH).coerceAtLeast(bar)
        val maxScroll = content - viewport
        val maxThumbY = (trackH - thumbH).coerceAtLeast(0f)
        val thumbY = box.y() + if (maxScroll <= 0f) 0f else sc.scrollY() / maxScroll * maxThumbY
        bridge.fillRect(trackX, box.y(), trackW, trackH, track)
        bridge.fillRect(trackX, thumbY, trackW, thumbH, thumb)
    }

    private fun renderScrollbarX(sc: ScrollableContainer<*>, box: Box, bridge: UiRenderBridge) {
        val viewport = sc.viewportWidth()
        val content = sc.contentWidth()
        if (!shouldShow(sc, viewport, content) || content <= viewport) {
            return
        }
        val track = trackColor(sc)
        val thumb = thumbColor(sc)
        val bar = barWidth(sc)
        val trackY = box.y() + (box.height() - bar).coerceAtLeast(0f)
        val trackH = bar.coerceAtMost(box.height())
        val trackW = box.width()
        val thumbW = (viewport / content * trackW).coerceAtLeast(bar)
        val maxScroll = content - viewport
        val maxThumbX = (trackW - thumbW).coerceAtLeast(0f)
        val thumbX = box.x() + if (maxScroll <= 0f) 0f else sc.scrollX() / maxScroll * maxThumbX
        bridge.fillRect(box.x(), trackY, trackW, trackH, track)
        bridge.fillRect(thumbX, trackY, thumbW, trackH, thumb)
    }

    private companion object {
        private const val BAR_WIDTH = 3f

        /** 滚动条轨道半透明比例（保留原 0x66000000 = ~40% 的视觉浓度）。 */
        private const val TRACK_ALPHA = 0.4f

        /** 滚动条 thumb 半透明比例（保留原 0xCCFFFFFF = ~80% 的视觉浓度）。 */
        private const val THUMB_ALPHA = 0.8f

        /** 是否应显示滚动条：ALWAYS 恒显；AUTO 仅在内容溢出时显示。 */
        private fun shouldShow(sc: ScrollableContainer<*>, viewport: Float, content: Float): Boolean {
            if (sc.scrollbarMode() == ScrollableContainer.ScrollbarMode.ALWAYS) {
                return viewport > 0f
            }
            return viewport > 0f && content > viewport
        }

        /** thumb 颜色：DSL 显式 scrollbarColor 优先，否则主题 outline 80%。 */
        private fun thumbColor(sc: ScrollableContainer<*>): Int {
            val explicit = sc.scrollbarColor()
            if ((explicit ushr 24) != 0) {
                return explicit
            }
            return StateLayer.withAlpha(ThemeAccess.current().outline, THUMB_ALPHA)
        }

        /** track 颜色：DSL 显式 scrollbarTrackColor 优先，否则主题 outlineVariant 40%。 */
        private fun trackColor(sc: ScrollableContainer<*>): Int {
            val explicit = sc.scrollbarTrackColor()
            if ((explicit ushr 24) != 0) {
                return explicit
            }
            return StateLayer.withAlpha(ThemeAccess.current().outlineVariant, TRACK_ALPHA)
        }

        /** 滚动条粗细：DSL scrollbarWidth>0 用之，否则默认 BAR_WIDTH。 */
        private fun barWidth(sc: ScrollableContainer<*>): Float {
            val w = sc.scrollbarWidth()
            return if (w > 0f) w else BAR_WIDTH
        }
    }
}
